import React from 'react'
import { useNavigate, useParams } from 'react-router'
import { isValidPhoneNumber, parsePhoneNumber } from 'libphonenumber-js'

import useNameFormatter from '@divvy-web/hooks.usenameformatter'
import { FormattedMessage } from '@divvy-web/i18n'
import { FormTextInput } from '@divvy-web/skylab.form'
import { useForm } from '@divvy-web/skylab.react-hook-form'

import Divider from '@sputnik/components/Divider'
import { useGetCreditApplicationForReviewSuspenseQuery } from '@sputnik/pages/CreditApplicationPages/ReviewForm/GetCreditApplicationForReview.gql'
import SectionCollapsibleList from '@sputnik/pages/CreditApplicationPages/ReviewForm/SectionCollapsibleList'
import {
  hasEmptyValue,
  hasMissingRequiredField,
  toSentenceCase,
} from '@sputnik/pages/CreditApplicationPages/ReviewForm/utils'
import { formatDateForDisplay } from '@sputnik/pages/CreditApplicationPages/ReviewForm/utils'
import { PATHNAME_CONSTS } from '@sputnik/resources/constants'
import { useWebTracking } from '@sputnik/tracking/WebTrackingContext'

import styles from './styles.module.scss'

const CompanyOwnersReviewSection = () => {
  const navigate = useNavigate()
  const { appId } = useParams()
  const { control } = useForm()
  const [getClassName] = useNameFormatter('CompanyOwnersReviewSection')
  const { track } = useWebTracking()
  const { data } = useGetCreditApplicationForReviewSuspenseQuery({
    variables: {
      creditApplicationId: appId ?? '',
    },
  })

  const beneficialOwners = data?.creditApplication?.beneficialOwners || []

  const hasMissingInfo =
    !!data &&
    hasMissingRequiredField(
      beneficialOwners.flatMap((owner) => {
        const {
          firstName,
          lastName,
          dob,
          email,
          passportCountry,
          passportNumberLastFour,
          passportExpiration,
          phoneNumber,
          ssnLastFour,
          address,
        } = owner ?? {}

        const hasPassportInfo = (passportCountry || passportNumberLastFour || passportExpiration) && !ssnLastFour

        return [
          firstName,
          lastName,
          dob,
          email,
          phoneNumber,
          address?.street,
          address?.city,
          address?.state,
          address?.postalCode,
          ...(hasPassportInfo ? [passportCountry, passportNumberLastFour, passportExpiration] : [ssnLastFour]),
        ]
      }),
    )

  const handleOnEditClick = () => {
    track('se_signup__review_and_sign_edit_info__fired', {
      application_section: 'Company Owners',
      navigated_to_page: `${PATHNAME_CONSTS.PEOPLE_INFO_PATH}`,
    })
    navigate(`../${PATHNAME_CONSTS.PEOPLE_INFO_PATH}`)
  }

  return (
    <div className={`${styles[getClassName('')]} fs-unmask`}>
      <SectionCollapsibleList
        hasMissingInfo={hasMissingInfo}
        headerText={
          <FormattedMessage
            defaultMessage='Company owners'
            id='sputnik.CompanyOwnersReviewSection__YVlBeJ'
          />
        }
        onEditClick={handleOnEditClick}
      >
        {beneficialOwners.length === 0 && (
          <FormattedMessage
            defaultMessage='No company owners'
            id='sputnik.CompanyOwnersReviewSection__NoOwnersFound'
          />
        )}

        {beneficialOwners?.map((owner, index) => {
          const showPassportSection =
            (owner?.passportCountry || owner?.passportNumberLastFour || owner?.passportExpiration) &&
            !owner?.ssnLastFour
          return (
            <React.Fragment key={owner?.id}>
              <>
                <FormTextInput
                  readOnly
                  className='fs-mask'
                  control={control}
                  isRequired={hasEmptyValue(owner?.firstName)}
                  label={
                    <span className='fs-unmask'>
                      <FormattedMessage
                        defaultMessage='First name'
                        id='sputnik.CompanyOwnersReviewSection__16D5H3'
                      />
                    </span>
                  }
                  name={`companyOwners.${index}.firstName`}
                  value={owner?.firstName ? toSentenceCase(owner?.firstName) : '—'}
                />
                <FormTextInput
                  readOnly
                  className='fs-mask'
                  control={control}
                  isRequired={hasEmptyValue(owner?.lastName)}
                  label={
                    <span className='fs-unmask'>
                      <FormattedMessage
                        defaultMessage='Last name'
                        id='sputnik.CompanyOwnersReviewSection__dsX8S3'
                      />
                    </span>
                  }
                  name={`companyOwners.${index}.lastName`}
                  value={owner?.lastName ? toSentenceCase(owner?.lastName) : '—'}
                />

                <FormTextInput
                  readOnly
                  className='fs-mask'
                  control={control}
                  isRequired={hasEmptyValue(owner?.dob)}
                  label={
                    <span className='fs-unmask'>
                      <FormattedMessage
                        defaultMessage='Date of birth'
                        id='sputnik.CompanyOwnersReviewSection__GFTdXw'
                      />
                    </span>
                  }
                  name={`companyOwners.${index}.dob`}
                  value={owner?.dob ? formatDateForDisplay(owner?.dob) : '—'}
                />
                <FormTextInput
                  readOnly
                  className='fs-mask'
                  control={control}
                  isRequired={hasEmptyValue(owner?.email)}
                  label={
                    <span className='fs-unmask'>
                      <FormattedMessage
                        defaultMessage='Business email'
                        id='sputnik.CompanyOwnersReviewSection__Z7xDUp'
                      />
                    </span>
                  }
                  name={`companyOwners.${index}.email`}
                  value={owner?.email || '—'}
                />

                <span className={styles[getClassName('street')]}>
                  <FormTextInput
                    readOnly
                    className='fs-mask'
                    control={control}
                    isRequired={hasEmptyValue(owner?.address?.street)}
                    label={
                      <span className='fs-unmask'>
                        <FormattedMessage
                          defaultMessage='Residential address'
                          id='sputnik.CompanyOwnersReviewSection__4iXhxD'
                        />
                      </span>
                    }
                    name={`companyOwners.${index}.address.streetFull`}
                    value={
                      owner?.address?.street
                        ? `${owner?.address?.street || ''} ${owner?.address?.streetAdditional || ''}`
                        : '—'
                    }
                  />
                </span>
                <div className={styles[getClassName('city-state')]}>
                  <FormTextInput
                    readOnly
                    className='fs-mask'
                    control={control}
                    isRequired={hasEmptyValue(owner?.address?.city)}
                    label={
                      <span className='fs-unmask'>
                        <FormattedMessage
                          defaultMessage='City'
                          id='sputnik.CompanyOwnersReviewSection__TE4fIS'
                        />
                      </span>
                    }
                    name={`companyOwners.${index}.address.city`}
                    value={owner?.address?.city ? toSentenceCase(owner?.address?.city) : '—'}
                  />
                  <FormTextInput
                    readOnly
                    className='fs-mask'
                    control={control}
                    isRequired={hasEmptyValue(owner?.address?.state)}
                    label={
                      <span className='fs-unmask'>
                        <FormattedMessage
                          defaultMessage='State'
                          id='sputnik.CompanyOwnersReviewSection__ku+mDU'
                        />
                      </span>
                    }
                    name={`companyOwners.${index}.address.state`}
                    value={owner?.address?.state || '—'}
                  />
                </div>
                <FormTextInput
                  readOnly
                  className='fs-mask'
                  control={control}
                  isRequired={hasEmptyValue(owner?.address?.postalCode)}
                  label={
                    <span className='fs-unmask'>
                      <FormattedMessage
                        defaultMessage='ZIP code'
                        id='sputnik.CompanyOwnersReviewSection__iZL8VQ'
                      />
                    </span>
                  }
                  name={`companyOwners.${index}.address.postalCode`}
                  value={owner?.address?.postalCode || '—'}
                />
                <FormTextInput
                  readOnly
                  className='fs-mask'
                  control={control}
                  isRequired={hasEmptyValue(owner?.phoneNumber)}
                  label={
                    <span className='fs-unmask'>
                      <FormattedMessage
                        defaultMessage='Mobile phone number'
                        id='sputnik.CompanyOwnersReviewSection__h8O5Rm'
                      />
                    </span>
                  }
                  name={`companyOwners.${index}.phoneNumber`}
                  value={
                    owner?.phoneNumber
                      ? isValidPhoneNumber(owner?.phoneNumber)
                        ? parsePhoneNumber(owner?.phoneNumber).formatNational()
                        : owner?.phoneNumber
                      : '—'
                  }
                />
                {showPassportSection && (
                  <>
                    <FormTextInput
                      readOnly
                      className='fs-mask'
                      control={control}
                      isRequired={hasEmptyValue(owner?.passportNumberLastFour)}
                      label={
                        <span className='fs-unmask'>
                          <FormattedMessage
                            defaultMessage='Passport number'
                            id='sputnik.CompanyOwnersReviewSection__7DeEPg'
                          />
                        </span>
                      }
                      name={`companyOwners.${index}.passportNumberLastFour`}
                      value={owner?.passportNumberLastFour ? `****${owner?.passportNumberLastFour}` : '—'}
                    />
                    <FormTextInput
                      readOnly
                      className='fs-mask'
                      control={control}
                      isRequired={hasEmptyValue(owner?.passportCountry)}
                      label={
                        <span className='fs-unmask'>
                          <FormattedMessage
                            defaultMessage='Issuing country'
                            id='sputnik.CompanyOwnersReviewSection__GdcAVX'
                          />
                        </span>
                      }
                      name={`companyOwners.${index}.passportCountry`}
                      value={owner?.passportCountry || '—'}
                    />
                    <FormTextInput
                      readOnly
                      className='fs-mask'
                      control={control}
                      isRequired={hasEmptyValue(owner?.passportExpiration)}
                      label={
                        <span className='fs-unmask'>
                          <FormattedMessage
                            defaultMessage='Expiration date'
                            id='sputnik.CompanyOwnersReviewSection__CICBj0'
                          />
                        </span>
                      }
                      name={`companyOwners.${index}.passportExpiration`}
                      value={formatDateForDisplay(owner?.passportExpiration) || '—'}
                    />
                  </>
                )}
                {!showPassportSection && (
                  <FormTextInput
                    readOnly
                    className='fs-mask'
                    control={control}
                    formFieldClassName={styles[getClassName('ssn')]}
                    isRequired={hasEmptyValue(owner?.ssnLastFour)}
                    label={
                      <span className='fs-unmask'>
                        <FormattedMessage
                          defaultMessage='Social Security number'
                          id='sputnik.CompanyOwnersReviewSection__Pk5sml'
                        />
                      </span>
                    }
                    name={`companyOwners.${index}.ssnLastFour`}
                    value={owner?.ssnLastFour ? `***-**-${owner?.ssnLastFour}` : '—'}
                  />
                )}
              </>
              {index !== beneficialOwners.length - 1 && <Divider className={styles[getClassName('person-divider')]} />}
            </React.Fragment>
          )
        })}
      </SectionCollapsibleList>
    </div>
  )
}

export default CompanyOwnersReviewSection
